import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IOrderModel } from 'src/app/appModels/IOrderModel';
import { CartSessionEnvelopeService } from 'src/app/appStorage/cart-session-envelope.service';

@Injectable({
  providedIn: 'root'
})
export class CheckoutHelper {

  tipAmount = new BehaviorSubject(0);
  tipToggleVal = new BehaviorSubject<any>(null); // Tip Toggle Val is help to reset Tip amount or remove tip
  tax = new BehaviorSubject<any | null>(null);
  taxIncluded = new BehaviorSubject<boolean>(true);

  activeOrderId = new BehaviorSubject<number | null>(null);

  totalAmount = new BehaviorSubject<number>(0);

  /**
   * Idempotency key for the current cart: created once and reused across
   * retries of the same order (sent as `uuid`, prefix `idmp-`). The backend
   * returns the already-created order when it sees the same key again, so a
   * retry after a lost response cannot duplicate the order. Cleared whenever
   * the cart is cleared (success or abandonment) so the next customer starts
   * with a fresh key.
   */
  constructor(private cartSession: CartSessionEnvelopeService) {}

  getOrCreateClientOrderKey(): string {
    const session = this.cartSession.load();
    if (session.clientOrderKey) {
      return session.clientOrderKey;
    }
    const key = 'idmp-' + this.generateRandomKey();
    // Preserve the exact current items while assigning the key in the same
    // sessionStorage write. A WebView reload now recovers both together.
    this.cartSession.write(session.items, key);
    return key;
  }

  /**
   * Stable identity for the current customer's complete payment session.
   *
   * Cart edits deliberately rotate clientOrderKey because they change the
   * backend order payload. They must not rotate this key: otherwise editing
   * the basket would bypass a myPOS outcome that still needs reconciliation.
   * Clearing the cart removes the whole envelope and gives the next customer
   * a fresh key, even when they select exactly the same products.
   */
  getOrCreatePaymentSessionKey(): string {
    const session = this.cartSession.load();
    if (session.paymentSessionKey) {
      return session.paymentSessionKey;
    }
    // Rolling-update safety: stage_v2 used clientOrderKey itself as the
    // myPOS sale lock. Reusing it once when it already exists keeps a
    // pre-update UNKNOWN lock effective instead of orphaning/bypassing it.
    // Brand-new sessions use a dedicated identity from the outset.
    const key =
      session.clientOrderKey ||
      'idmp-pay-' + this.generateRandomKey();
    this.cartSession.write(session.items, session.clientOrderKey, key);
    return key;
  }

  clearClientOrderKey(): void {
    const session = this.cartSession.load();
    this.cartSession.write(session.items, null);
  }

  private generateRandomKey(): string {
    const cryptoObj: any = (window as any).crypto;
    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID();
    }
    if (cryptoObj?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoObj.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (value) =>
        value.toString(16).padStart(2, '0'),
      );
      return [
        hex.slice(0, 4).join(''),
        hex.slice(4, 6).join(''),
        hex.slice(6, 8).join(''),
        hex.slice(8, 10).join(''),
        hex.slice(10, 16).join(''),
      ].join('-');
    }
    throw new Error('A cryptographically secure random generator is required.');
  }

}
